import React, { useState } from 'react';
import { useDeletePiece } from '../hooks/usePieces';

interface BulkDeletePiecesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPieces: any[];
  onSuccess: () => void;
}

const BulkDeletePiecesModal: React.FC<BulkDeletePiecesModalProps> = ({
  isOpen,
  onClose,
  selectedPieces,
  onSuccess
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const deletePiece = useDeletePiece();

  const handleDelete = async () => {
    setIsLoading(true);

    try {
      const deletePromises = selectedPieces.map(piece => 
        deletePiece.mutateAsync(piece.id)
      );

      await Promise.all(deletePromises);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur lors de la suppression en masse:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-lg font-semibold mb-4 text-red-600">
          Supprimer {selectedPieces.length} pièce{selectedPieces.length > 1 ? 's' : ''}
        </h2>

        <div className="mb-6">
          <p className="text-gray-700 mb-4">
            Êtes-vous sûr de vouloir supprimer {selectedPieces.length} pièce{selectedPieces.length > 1 ? 's' : ''} ?
          </p>
          
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800">
              ⚠️ <strong>Attention :</strong> Cette action supprimera également toutes les zones, 
              boxes et objets associés à ces pièces.
            </p>
          </div>

          <div className="mt-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Pièces à supprimer :</p>
            <ul className="text-sm text-gray-600 max-h-32 overflow-y-auto">
              {selectedPieces.map(piece => (
                <li key={piece.id} className="py-1">• {piece.nom}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleDelete}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {isLoading ? 'Suppression...' : 'Supprimer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkDeletePiecesModal;
